/**
 * FRONT-2598 — the panel's end of the private channel to the edit frame
 * (the page's end is src/artifacts/editor-script.ts).
 *
 * `window.postMessage` is a shared mailbox: any script in the framed page, and
 * any other window that can reach this one, can post something shaped like an
 * edit message. So the page opens a `MessageChannel`, hands the panel one end
 * in a single window-level `eddy-edit-hello`, and every later message —
 * ready, change, restore, goto, lock — travels over that channel, which only
 * the two of them hold.
 *
 * Rules, each pinned by edit-frame-link.test.ts:
 *  - only a hello whose `source` is the frame's own window is looked at;
 *  - only the FIRST hello of a frame load is accepted (a second hello from the
 *    same page cannot swap the channel). A second hello from the frame's OWN
 *    window means the framed page navigated itself (a meta refresh, a
 *    `location` assignment): the offered port is closed, the channel is left
 *    alone, and `onNavigated` is called so the panel can remount the frame;
 *  - once connected, every window-level message is ignored, hello or not;
 *  - `reset()` forgets the channel. The panel calls it from the iframe's ref
 *    callback when a NEW element attaches (not earlier: until React swaps the
 *    element the old page is still the frame and a late hello from it must not
 *    be taken for the new one), never on the iframe's `load` event:
 *    the page posts its hello while it parses, which is before `load`, so a
 *    reset on `load` would throw away the very channel it just received.
 */

export interface LinkPort {
  postMessage(message: unknown): void;
  // `any`: a real MessagePort's handler takes a full MessageEvent; the link reads only `.data`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onmessage: ((event: any) => void) | null;
  close?(): void;
}

export interface LinkWindowEvent {
  source: unknown;
  data: unknown;
  ports?: ReadonlyArray<LinkPort>;
}

export interface FrameLink {
  /** True exactly when this event was the accepted hello (the channel is now connected). */
  handleWindowMessage(event: LinkWindowEvent, frameWindow: unknown): boolean;
  /** Send to the page; false when no channel is connected yet. */
  send(message: Record<string, unknown>): boolean;
  readonly connected: boolean;
  reset(): void;
}

export function createFrameLink(onMessage: (data: unknown) => void, onNavigated?: () => void): FrameLink {
  let port: LinkPort | null = null;
  let accepted = false;

  return {
    handleWindowMessage(event, frameWindow) {
      if (!frameWindow || event.source !== frameWindow) return false;
      const d = event.data as { type?: unknown } | null;
      if (!d || typeof d !== 'object' || d.type !== 'eddy-edit-hello') return false;
      const offered = event.ports?.[0];
      if (!offered) return false;
      if (accepted) {
        offered.close?.();
        onNavigated?.();
        return false;
      }
      accepted = true;
      port = offered;
      port.onmessage = (e) => {
        if (port === offered) onMessage(e.data);
      };
      return true;
    },
    send(message) {
      if (!port) return false;
      port.postMessage(message);
      return true;
    },
    get connected() {
      return port !== null;
    },
    reset() {
      if (port) {
        port.onmessage = null;
        port.close?.();
      }
      port = null;
      accepted = false;
    },
  };
}
